import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Building2, Eye, EyeOff, Loader2, MailX, ShieldCheck } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { APP_ROLE_LABEL, type AppRole } from "@rn/contracts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { api, ApiError, tokens } from "@/lib/api";

export const Route = createFileRoute("/convite/$token")({
  component: ConvitePage,
});

type Preview = { email: string; role: AppRole; tenantName: string | null };

/**
 * Aceite de convite.
 *
 * Rota pública: quem chega não tem sessão. A autorização é o token na URL, que
 * só o convidado recebeu. A prévia existe para a pessoa confirmar de qual
 * empresa é o convite antes de criar a senha.
 */
function ConvitePage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [senhaVisivel, setSenhaVisivel] = useState(false);

  const preview = useQuery({
    queryKey: ["invite", token],
    queryFn: () => api.get<Preview>(`/invites/${token}`),
    retry: false,
  });

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setErrors({});
    const form = new FormData(event.currentTarget);

    try {
      await api.anonymous("/invites/accept", {
        token,
        fullName: String(form.get("fullName") ?? ""),
        password: String(form.get("password") ?? ""),
      });
      // O aceite cria a conta mas não abre sessão: a pessoa entra com a senha
      // que acabou de definir, o que confirma que ela a memorizou.
      tokens.clear();
      toast.success("Conta criada. Entre com sua senha.");
      await navigate({ to: "/entrar" });
    } catch (error) {
      if (error instanceof ApiError) {
        const byField = error.fieldErrors;
        if (Object.keys(byField).length > 0) setErrors(byField);
        else toast.error(error.message);
      } else {
        toast.error("Não foi possível concluir");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (preview.isLoading) {
    return (
      <Moldura>
        {/* Esqueleto com a forma do formulário: a tela não pula quando a
            prévia chega. */}
        <Card className="shadow-panel" role="status" aria-label="Verificando o convite">
          <CardContent className="space-y-5 p-6 sm:p-8">
            <div className="space-y-2">
              <Skeleton className="h-7 w-44" />
              <div className="flex gap-2">
                <Skeleton className="h-5 w-32 rounded-full" />
                <Skeleton className="h-5 w-24 rounded-full" />
              </div>
            </div>
            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-1.5">
                <Skeleton className="h-3.5 w-20" />
                <Skeleton className="h-9 w-full" />
              </div>
            ))}
            <Skeleton className="h-10 w-full" />
            <p className="sr-only">Verificando o convite…</p>
          </CardContent>
        </Card>
      </Moldura>
    );
  }

  if (preview.isError) {
    return (
      <Moldura>
        <Card className="shadow-panel">
          <CardContent className="p-6 sm:p-8">
            <EmptyState
              compact
              icon={MailX}
              title="Convite inválido ou expirado"
              description="Convites valem por 7 dias e só podem ser usados uma vez. Peça um novo para quem convidou você."
              action={
                <Button asChild variant="outline">
                  <Link to="/entrar">Ir para o login</Link>
                </Button>
              }
            />
          </CardContent>
        </Card>
      </Moldura>
    );
  }

  const erroSenha = errors["password"];

  return (
    <Moldura>
      <Card className="shadow-panel">
        <CardContent className="p-6 sm:p-8">
          <div className="mb-6">
            <h1 className="font-display text-xl font-semibold leading-tight tracking-tight sm:text-2xl">
              Você foi convidado
            </h1>
            {/* Empresa e papel como chips: é o que a pessoa precisa conferir
                antes de criar a senha. */}
            <div className="mt-3 flex flex-wrap gap-1.5">
              {preview.data?.tenantName && (
                <Badge variant="soft">
                  <Building2 />
                  {preview.data.tenantName}
                </Badge>
              )}
              {preview.data && (
                <Badge variant="info">
                  <ShieldCheck />
                  {APP_ROLE_LABEL[preview.data.role]}
                </Badge>
              )}
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Confirme seu nome e crie uma senha para entrar na equipe.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">E-mail</Label>
              {/* Não editável: o convite foi emitido para este endereço. */}
              <Input id="email" value={preview.data?.email ?? ""} disabled />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="fullName">Seu nome</Label>
              <Input
                id="fullName"
                name="fullName"
                autoComplete="name"
                required
                aria-invalid={Boolean(errors["fullName"])}
                aria-describedby={errors["fullName"] ? "fullName-erro" : undefined}
              />
              {errors["fullName"] && (
                <p id="fullName-erro" className="text-xs text-destructive">
                  {errors["fullName"]}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Crie uma senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={senhaVisivel ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  className="pr-10"
                  aria-invalid={Boolean(erroSenha)}
                  aria-describedby="password-dica"
                />
                {/* Senha de 12 caracteres digitada no celular erra com
                    frequência; poder conferir evita a segunda tentativa. */}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="absolute right-0.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:bg-transparent hover:text-foreground"
                  aria-label={senhaVisivel ? "Ocultar senha" : "Mostrar senha"}
                  aria-pressed={senhaVisivel}
                  onClick={() => setSenhaVisivel((v) => !v)}
                >
                  {senhaVisivel ? <EyeOff /> : <Eye />}
                </Button>
              </div>
              <p
                id="password-dica"
                className={erroSenha ? "text-xs text-destructive" : "text-xs text-muted-foreground"}
              >
                {erroSenha ?? "Mínimo de 12 caracteres"}
              </p>
            </div>

            <Button type="submit" size="lg" className="mt-2 w-full" disabled={submitting}>
              {submitting && <Loader2 className="animate-spin" />}
              {submitting ? "Criando…" : "Criar minha conta"}
            </Button>
          </form>

          <p className="mt-5 text-center text-sm text-muted-foreground">
            Já tem conta?{" "}
            <Button asChild variant="link" className="h-auto p-0 text-sm">
              <Link to="/entrar">Entrar</Link>
            </Button>
          </p>
        </CardContent>
      </Card>
    </Moldura>
  );
}

/**
 * Moldura da tela pública — a mesma do login.
 *
 * Painel de marca à esquerda em tela grande (faixa curta no celular) e o
 * conteúdo centralizado à direita. Vive aqui, e não num componente
 * compartilhado, porque arquivos de rota são divididos em chunks pelo
 * roteador e não devem exportar nada além de `Route`.
 */
function Moldura({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col bg-background lg:grid lg:grid-cols-2">
      <aside className="relative isolate overflow-hidden bg-sidebar text-sidebar-foreground lg:sticky lg:top-0 lg:h-dvh">
        {/* Brilhos decorativos, fora da árvore de acessibilidade. */}
        <div
          aria-hidden
          className="bg-gradient-brand pointer-events-none absolute -left-24 -top-32 -z-10 size-[28rem] rounded-full opacity-25 blur-3xl"
        />
        <div
          aria-hidden
          className="bg-gradient-brand pointer-events-none absolute -bottom-40 -right-24 -z-10 hidden size-96 rounded-full opacity-15 blur-3xl lg:block"
        />

        {/* Celular: só a marca. */}
        <div className="flex items-center gap-2.5 px-4 py-4 sm:px-6 lg:hidden">
          <BrandMark />
          <span className="font-display text-[15px] font-semibold text-sidebar-accent-foreground">
            Revenue Nexus
          </span>
        </div>

        {/* Tela grande: marca, promessa e rodapé. */}
        <div className="hidden h-full flex-col justify-between gap-10 p-10 lg:flex xl:p-14">
          <div className="flex items-center gap-2.5">
            <BrandMark />
            <span className="font-display text-[15px] font-semibold text-sidebar-accent-foreground">
              Revenue Nexus
            </span>
          </div>

          <div className="max-w-md">
            <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-sidebar-accent-foreground xl:text-4xl">
              O CRM que sabe de onde veio cada paciente.
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-sidebar-foreground/70">
              Conversas, agenda e pipeline da clínica num lugar só — e cada venda ligada ao anúncio
              que a trouxe.
            </p>
          </div>

          <LegalLinks
            className="text-sidebar-foreground/55"
            linkClassName="hover:text-sidebar-accent-foreground"
          />
        </div>
      </aside>

      <main className="flex flex-1 flex-col items-center justify-center px-4 py-10 sm:px-6 lg:py-12">
        <div className="w-full max-w-sm animate-rise">
          {children}
          {/* No celular o painel é só uma faixa; os links legais ficam aqui. */}
          <LegalLinks
            className="mt-6 justify-center text-muted-foreground lg:hidden"
            linkClassName="hover:text-foreground"
          />
        </div>
      </main>
    </div>
  );
}

/** O quadrado "RN" — o mesmo do cabeçalho da barra lateral. */
function BrandMark() {
  return (
    <span
      aria-hidden
      className="bg-gradient-brand inline-flex size-8 shrink-0 items-center justify-center rounded-lg font-display text-[11px] font-bold tracking-tight text-white shadow-card"
    >
      RN
    </span>
  );
}

/** Rodapé legal: ano, privacidade e termos. As cores vêm de quem usa. */
function LegalLinks({ className, linkClassName }: { className: string; linkClassName: string }) {
  const link = `transition-colors ${linkClassName}`;
  return (
    <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ${className}`}>
      <span>© {new Date().getFullYear()} Revenue Nexus</span>
      <Link to="/privacidade" className={link}>
        Privacidade
      </Link>
      <Link to="/termos" className={link}>
        Termos
      </Link>
    </div>
  );
}
